import {
  getTablePermissions,
  getIgnoredColumnsByPermission,
  filterColumnsByPermission,
  hasPermission,
} from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";

export type MarketOption = { value: string; label: string };

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  projects: "projects",
  markets: "markets",
} as const;

export const getClientTableConfig = (
  userRoles: UserRoleType[] | null,
  marketOptions: MarketOption[] = [],
  displayNames?,
) => {
  const permissions = getTablePermissions(userRoles, "clients");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  const allFilterChoices = [
    {
      name: "id",
      kind: "text" as const,
      displayName: `${displayNames?.clients?.name ?? "Client"} Id`,
      menus: ["filter", "visible"] as const,
    },
    { name: "name", kind: "text" as const, menus: ["filter", "visible"] as const },
    {
      name: "status",
      kind: "enum" as const,
      options: [
        { value: "ACTIVE", color: "complete" },
        { value: "INACTIVE", color: "neutral" },
      ],
      menus: ["filter", "visible"] as const,
    },
    { name: "projects", kind: "number" as const, menus: ["filter", "visible"] as const },
    { name: "description", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
    {
      name: "markets",
      kind: "array" as const,
      displayName: "Markets",
      options: marketOptions,
      menus: ["filter", "visible", "groupby"] as const,
    },
  ];

  return {
    entityName: "Client",
    entityNamePlural: "Clients",

    tableConfig: {
      ignore: ["tenant_id", "meta_data", "comments", "addresses", "history", ...permissionIgnored],
      readonly: ["id", "created_at", "projects"],
      validators: {},
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.clients?.name ?? "Client"} Id`;
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "projects") return value[0].count;
        if (key === "incidents" && Array.isArray(value)) return value.map((i: any) => i.name).join(", ");
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date",
        status: {
          type: "enum",
          options: [
            { value: "ACTIVE", color: "complete" },
            { value: "INACTIVE", color: "neutral" },
          ],
        },
        incidents: {
          type: "array" as const,
          picker: "incident" as const,
          chipColor: "#DC3545",
          options: [],
        },
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: {
            type: "array" as const,
            picker: "market" as const,
            chipColor: "#4169E1",
            options: marketOptions,
          },
        }),
      },
      writeKeys: {
        incidents: "meta_data.incidents",
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: "meta_data.markets",
        }),
      },
    },
    filterChoices: filterColumnsByPermission(userRoles, allFilterChoices, RELATIONSHIP_COLUMNS),
    ...permissions,
    storageKey: "client_flyout",
  };
};
